/**
 * Стадии «Проката», салоны и мероприятия (`GET /api/deals/stages`), перевод сделки в стадию
 * (`POST /api/deals/{id}/stage`), дата обработки (`POST /api/deals/{id}/wait`), даты проката (`POST /api/deals/{id}/rent`),
 * город (`POST /api/deals/{id}/location`), мероприятие и его дата (`POST /api/deals/{id}/event`),
 * комментарий в ленту сделки (`POST /api/deals/{id}/comment`), создание сделки
 * из чата (`POST /api/chats/{id}/deal`) или из лида (`POST /api/leads/{id}/deal`) и журнал действий сотрудника в панели
 * (`user_action`: `GET`/`POST /api/chats/{id}/actions`). Записи идут в боевой Bitrix с
 * журналом `bitrix_write_log`; каждое действие, включая неудачное, — строка `user_action`.
 */
import { apiFetch } from "@/lib/api";

/** work / wait / think — стадии КЦ, refuse — отказ, won — успех, other — бронь, возврат и т. п. */
export type StageKind = "work" | "wait" | "think" | "refuse" | "won" | "other";
/** `short` — короткое название из CRM (`b24DealStage.stageShort`: «Ждем Клиента» → «Ждем») для
 *  тесных мест вроде панели расширения; сервер без него отдаёт полное, берём `s.short || s.name`. */
export type Stage = { code: string; name: string; short: string; kind: StageKind };
export type Location = { id: number; name: string; short: string; bitrix_id: number };
/** Значение списка «Мероприятие» сделки в Bitrix. */
export type DealEvent = { id: number; name: string };
/** Куда создать сделку: «Прокат» (0) или «Приём платьев» (2, «Сдача» Bitrix) — свои стадии создания. */
export type Direction = { id: number; name: string; stages: Stage[]; default: string | null };
export type StageSet = { stages: Stage[]; locations: Location[]; events: DealEvent[]; directions: Direction[] };
export type StageResult = {
  id: number;
  stage: string;
  wait_at: string | null;
  was: { stage: string | null; wait_at: string | null };
  /** Bitrix не ответил — правку дошлёт очередь (полоса `BitrixPending`). */
  pending?: boolean;
};
export type WaitResult = { id: number; wait_at: string; was: { wait_at: string | null }; pending?: boolean };
export type RentResult = {
  id: number;
  rent_start: string;
  rent_end: string;
  was: { rent_start: string | null; rent_end: string | null };
};
export type NewDeal = {
  phone: string;
  name?: string;
  stage: string;
  wait_at?: string | null;
  location?: number | null;
  /** Направление `Direction.id`; нет — «Прокат». */
  category?: number;
};
/** `lead_id` — лид «Не обработан» / «В работе» по номеру, который конвертировали в эту сделку. */
export type NewDealResult = {
  /** Пусто — Bitrix не ответил, сделку создаст очередь (`pending`, команда `command`). */
  id: number | null;
  contact_id: number;
  stage: string;
  wait_at: string | null;
  lead_id: number | null;
  lead_converted: boolean;
  lead_error: string;
  pending: boolean;
  command?: number;
};
/** Действия, о которых сервер сам не узнал бы, — панель сообщает их отдельно. */
export type ClientAction = "open_bitrix" | "open_deal" | "open_crm";
export type UserAction = {
  id: number;
  at: string;
  user_id: number | null;
  user: string;
  screen: string;
  chat_id: number | null;
  deal_id: number | null;
  action: string;
  data: Record<string, unknown>;
  error: string;
};

export const loadStages = () => apiFetch<StageSet>("/api/deals/stages");
export const setDealStage = (dealId: number, stage: string, wait_at: string | null, chatId?: number) =>
  apiFetch<StageResult>(`/api/deals/${dealId}/stage`, {
    method: "POST",
    body: JSON.stringify({ stage, wait_at, chat_id: chatId ?? null }),
  });
/** Только дата записи / следующей обработки, стадия прежняя; `wait_at` — `ГГГГ-ММ-ДДTЧЧ:ММ` без пояса. */
export const setDealWait = (dealId: number, wait_at: string, chatId?: number) =>
  apiFetch<WaitResult>(`/api/deals/${dealId}/wait`, {
    method: "POST",
    body: JSON.stringify({ wait_at, chat_id: chatId ?? null }),
  });
export const setDealRent = (dealId: number, start: string, end: string, chatId?: number) =>
  apiFetch<RentResult>(`/api/deals/${dealId}/rent`, {
    method: "POST",
    body: JSON.stringify({ start, end, chat_id: chatId ?? null }),
  });
/** Город сделки: `location` — id салона с кассой из `StageSet.locations`. */
export const setDealLocation = (dealId: number, location: number, chatId?: number) =>
  apiFetch<{ id: number; location: Location }>(`/api/deals/${dealId}/location`, {
    method: "POST",
    body: JSON.stringify({ location, chat_id: chatId ?? null }),
  });
/** Мероприятие (`event` — id из `StageSet.events`) и/или его дата `ГГГГ-ММ-ДД`: что дано, то и пишется. */
export const setDealEvent = (dealId: number, p: { event?: number; date?: string }, chatId?: number) =>
  apiFetch<{ id: number; event: number | null; event_date: string | null }>(`/api/deals/${dealId}/event`, {
    method: "POST",
    body: JSON.stringify({ event: p.event ?? null, date: p.date ?? null, chat_id: chatId ?? null }),
  });
/** Комментарий к сделке — в очередь CRM, оттуда в ленту Bitrix; `text` в ответе — как записано,
 * с именем сотрудника, `comment_id` — строка очереди `b24Deal_comment`. */
export const addDealComment = (dealId: number, text: string, chatId?: number) =>
  apiFetch<{ id: number; comment_id: number; text: string }>(`/api/deals/${dealId}/comment`, {
    method: "POST",
    body: JSON.stringify({ text, chat_id: chatId ?? null }),
  });
export const createDealFromChat = (chatId: number, body: NewDeal) =>
  apiFetch<NewDealResult>(`/api/chats/${chatId}/deal`, { method: "POST", body: JSON.stringify(body) });
/** Сделка из лида открытой карточки Bitrix (панель расширения): чата нет, номер сервер берёт у лида. */
export const createDealFromLead = (leadId: number, body: Omit<NewDeal, "phone">) =>
  apiFetch<NewDealResult>(`/api/leads/${leadId}/deal`, { method: "POST", body: JSON.stringify(body) });
/** Сделка по номеру из карточки номера в «Чатах» («Звонки», всплывашка «Входящий»): ни чата, ни лида;
 * `call_id` — `bitrix_call.id` звонка, 0 — звонок ещё идёт или номер из поиска. */
export const createDealByPhone = (callId: number, body: NewDeal) =>
  apiFetch<NewDealResult>("/api/deals/phone", { method: "POST", body: JSON.stringify({ ...body, call_id: callId }) });
/** Ссылка на самозапись `my.uni-renter.ru/z/<хэш>` — клиенту в чат (пишет на боевую площадку). */
export const sendZapisLink = (chatId: number) =>
  apiFetch<{ url: string; text: string; sent: boolean }>(`/api/chats/${chatId}/zapis-link`, { method: "POST" });
/** Та же ссылка с рекомендованным временем (окно «Время записи»): клиенту не уходит — текст в поле ответа. */
export const zapisLinkText = (chatId: number, location: number, picked: Record<string, number[]>) =>
  apiFetch<{ url: string; text: string; sent: boolean }>(`/api/chats/${chatId}/zapis-link`, {
    method: "POST",
    body: JSON.stringify({ send: false, location, picked }),
  });
/** Заготовка со ссылкой на запись (кнопка-молния у поля ответа): клиенту не уходит — текст в поле.
 *  `later` — «как определитесь со временем», `invite` — обычное приглашение выбрать день и час. */
export type ZapisDraft = "invite" | "later";
export const zapisLinkDraft = (chatId: number, kind: ZapisDraft) =>
  apiFetch<{ url: string; text: string; sent: boolean }>(`/api/chats/${chatId}/zapis-link`, {
    method: "POST",
    body: JSON.stringify({ send: false, kind }),
  });
/** Страница брони клиента `my.uni-renter.ru/d/<хэш>` — даты, платья, оплата, повторная примерка — текст в поле;
 *  `refit` — её посадочная «Повторная примерка» (`/d/<хэш>/refit`): записаться или изменить запись;
 *  `delivery` — та же страница на строке «Получение» (`#start`): доставка курьером. */
export const dealPageLinkDraft = (chatId: number, page: "deal" | "refit" | "delivery" = "deal") =>
  apiFetch<{ deal_id: number; url: string; text: string }>(`/api/chats/${chatId}/deal-link`, {
    method: "POST",
    body: JSON.stringify({ page }),
  });
export type PaymentQrForm = {
  deal_id: number;
  city: string | null;
  qr: boolean;
  money: { total: number; paid: number; due: number };
};
/** Итог по сделке чата для молнии «QR на оплату»: итого, оплачено, к оплате и есть ли QR-касса у салона. */
export const paymentQrForm = (chatId: number) => apiFetch<PaymentQrForm>(`/api/chats/${chatId}/payment-qr`);
/** Счёт QR-кодом в боевой CRM (неоплаченный на ту же сумму — он же) — текст и JPEG (base64) в ответ клиенту. */
export const paymentQrCreate = (chatId: number, amount: number) =>
  apiFetch<{ payment_id: number; reused: boolean; amount: number; text: string; qr: string; qr_type: string }>(
    `/api/chats/${chatId}/payment-qr`,
    { method: "POST", body: JSON.stringify({ amount }) },
  );
/** Клиент хочет сдать платье: ссылки на ботов Telegram и MAX с привязкой к этому чату — текст в поле. */
export const sdatLinkDraft = (chatId: number) =>
  apiFetch<{ text: string; links: Record<string, string>; start: string }>(`/api/chats/${chatId}/sdat-link`, {
    method: "POST",
  });
export const logChatAction = (chatId: number, action: ClientAction, dealId?: number) =>
  apiFetch<{ ok: boolean }>(`/api/chats/${chatId}/actions`, {
    method: "POST",
    body: JSON.stringify({ action, deal_id: dealId ?? null }),
  });
export const loadChatActions = (chatId: number, dealId?: number) =>
  apiFetch<{ actions: UserAction[] }>(`/api/chats/${chatId}/actions${dealId ? `?deal=${dealId}` : ""}`);

/** `2026-10-03T15:00:00` из карточки → значение поля datetime-local. */
export const toLocalInput = (iso: string | null | undefined) => (iso ? iso.slice(0, 16) : "");
/** `2026-10-05` → `05.10.2026`. */
export const day = (iso: string | null | undefined) =>
  iso && iso.length >= 10 ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : "—";
/** Дней проката включительно: 05.10–07.10 → 3. */
export const rentDays = (start: string | null, end: string | null) =>
  start && end ? Math.round((Date.parse(end) - Date.parse(start)) / 86_400_000) + 1 : null;
